import React, { useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import {
  AlertCircle,
  CheckCircle2,
  ImagePlus,
  MessageSquare,
  Pencil,
  ReceiptText,
  Upload,
  X,
} from "lucide-react";
import { uploadItemImage } from "../../../Utils/imageUploadUtils";

import useClients from "../../Services/ClientServices/ClientConfig/useClientConfig";
import useOrderSettings from "../../Services/OrderServices/OrderSettingsServices/useOrderSettings";
import { GlobalContext } from "../../../Context/GlobalContext";

const THEME_BLUE_BG = "bg-[#f8fbff]";
const BORDER_SMART = "border-gray-300";

const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const formatTax = (v) => {
  const n = toNumber(v);
  return n ? n.toFixed(2) : "0.00";
};

export default function ClientMoreTab({ clientId, clientName }) {
  const { anlookApi } = useContext(GlobalContext);

  const { getGSTDetails, updateGSTDetails, busy } = useClients();

  const { getOrganizationDetails, editOrganizationInfo } = useOrderSettings();

  const [organization, setOrganization] = useState(null);
  const [gstForm, setGstForm] = useState({
    gst: "",
    gstType: "incl",
    taxGst: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [gstModalOpen, setGstModalOpen] = useState(false);

  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [savingGst, setSavingGst] = useState(false);

  const halfTax = useMemo(() => {
    const total = toNumber(gstForm.taxGst);
    return total / 2;
  }, [gstForm.taxGst]);

  const loadDetails = async () => {
    if (!clientId) return;

    setLoading(true);
    setError(null);

    try {
      const orgRes = await getOrganizationDetails(clientId);
      setOrganization(orgRes?.data || null);
    } catch (e) {
      console.error(e);
      setOrganization(null);
    }

    try {
      const gstData = await getGSTDetails(clientId);
      setGstForm({
        gst: gstData?.gst || "",
        gstType: gstData?.gst_type || "incl",
        taxGst:
          gstData?.tax_gst !== null && gstData?.tax_gst !== undefined
            ? String(gstData.tax_gst)
            : "",
      });
    } catch (e) {
      console.error(e);
      setGstForm({
        gst: "",
        gstType: "incl",
        taxGst: "",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDetails();
  }, [clientId]);

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!clientId) {
      toast.error("Client id missing");
      e.target.value = "";
      return;
    }

    try {
      setUploading(true);

      // Local preview
      const localPreview = URL.createObjectURL(file);
      setPreview(localPreview);

      // Common upload utility
      const result = await uploadItemImage({
        anlookApi,
        userid: clientId,
        itemName: "organization_image",
        file,
        maxSizeMB: 5,
      });

      const imageUrl = result?.imageUrl;

      if (!imageUrl) {
        throw new Error("Image URL missing");
      }

      const organizationPayload = {
        userid: clientId,
        organizationName:
          organization?.organizationName ||
          organization?.organization_name ||
          clientName ||
          "",
        gst: organization?.gst || gstForm.gst || "",
        pan_no: organization?.pan_no || "",
        brand_image: imageUrl,
        pament_gateway:
          organization?.pament_gateway || organization?.payment_gateway || "",
        opening_time: organization?.opening_time || "",
        closing_time: organization?.closing_time || "",
      };

      const updated = await editOrganizationInfo(organizationPayload);

      if (!updated?.success) {
        throw new Error(updated?.message || "Image update failed");
      }

      setOrganization((prev) => ({
        ...(prev || {}),
        ...organizationPayload,
        organization_name: organizationPayload.organizationName,
      }));

      setPreview(imageUrl);
      setImageModalOpen(false);

      toast.success("Organization image updated successfully");
    } catch (error) {
      console.error("Organization image update error:", error);

      toast.error(error?.message || "Failed to update organization image");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleGstUpdate = async () => {
    if (!clientId) {
      toast.error("Client id missing");
      return;
    }

    if (!gstForm.gst?.trim()) {
      toast.error("Please enter GST number");
      return;
    }

    if (!gstForm.taxGst?.trim()) {
      toast.error("Please enter total GST percentage");
      return;
    }

    if (Number.isNaN(Number(gstForm.taxGst)) || Number(gstForm.taxGst) < 0) {
      toast.error("Please enter valid GST percentage");
      return;
    }

    setSavingGst(true);
    try {
      await updateGSTDetails({
        userid: clientId,
        gst: gstForm.gst,
        gstType: gstForm.gstType,
        taxGst: gstForm.taxGst,
      });

      setGstModalOpen(false);
    } finally {
      setSavingGst(false);
    }
  };

  if (loading) return <LoaderSkeleton />;

  if (error) {
    return (
      <div
        className={`flex min-h-[400px] flex-col items-center justify-center text-gray-400 ${THEME_BLUE_BG}`}
      >
        <AlertCircle size={40} className="mb-3 opacity-20" />
        <p className="text-sm font-medium">{error}</p>
      </div>
    );
  }

  return (
    <div className={`min-h-[calc(100vh-120px)] w-full ${THEME_BLUE_BG} pb-20`}>
      <div className={`border-b bg-white px-6 py-6 shadow-sm ${BORDER_SMART}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="grid h-12 w-12 place-items-center rounded-xl bg-[#075E54]/10">
              <MessageSquare className="text-[#075E54]" size={24} />
            </div>

            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                Changes & Updates
              </h1>
              <p className="text-sm font-medium text-gray-500">
                Organization Image & GST Settings •{" "}
                <span className="font-mono text-xs">{clientId}</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <main className="space-y-6 px-0 py-8">
        <div className="relative mb-14 h-40 w-full rounded-3xl border-b-4 bg-gradient-to-r from-[#00c57c] to-[#03ac6e] shadow-lg">
          <div className="absolute -bottom-8 left-8 flex items-end gap-6">
            <div className="relative h-32 w-32 overflow-hidden rounded-2xl border-4 border-white bg-white shadow-xl">
              {preview || organization?.brand_image ? (
                <img
                  src={preview || organization?.brand_image}
                  alt="organization"
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="grid h-full w-full place-items-center bg-gray-100 text-gray-400">
                  <ImagePlus size={40} />
                </div>
              )}

              <button
                onClick={() => setImageModalOpen(true)}
                className="absolute bottom-2 right-2 rounded-full bg-white p-2 shadow-md hover:bg-gray-100"
              >
                <Pencil size={14} />
              </button>
            </div>

            <div className="pb-2">
              <div className="text-lg font-bold text-white drop-shadow">
                {organization?.organization_name ||
                  clientName ||
                  "Organization"}
              </div>
              <div className="mt-1 text-xs font-semibold text-white/90">
                Update Image URL Organization
              </div>
            </div>
          </div>
        </div>

        <Card title="Organization Details" icon={MessageSquare}>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <InfoBox
              label="Organization Name"
              value={
                organization?.organizationName ||
                organization?.organization_name ||
                clientName ||
                "—"
              }
            />
            <InfoBox label="PAN No." value={organization?.pan_no || "—"} />
            <InfoBox
              label="Payment Gateway"
              value={
                organization?.pament_gateway ||
                organization?.payment_gateway ||
                "—"
              }
            />
            <InfoBox
              label="Opening / Closing"
              value={`${organization?.opening_time || "—"} / ${
                organization?.closing_time || "—"
              }`}
            />
          </div>
        </Card>

        <Card
          title="GST Details"
          icon={ReceiptText}
          rightAction={
            <button
              onClick={() => setGstModalOpen(true)}
              className="rounded-xl bg-[#075E54] px-4 py-2 text-xs font-semibold text-white hover:opacity-90"
            >
              Edit GST
            </button>
          }
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <InfoBox label="GST No." value={gstForm.gst || "—"} />
            <InfoBox
              label="GST Type"
              value={gstForm.gstType === "excl" ? "Excl" : "Incl"}
            />
            <InfoBox label="SGST" value={`${formatTax(halfTax)}%`} />
            <InfoBox label="CGST" value={`${formatTax(halfTax)}%`} />
          </div>

          {/* <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-xs font-medium text-emerald-700">
            Total GST is {formatTax(gstForm.taxGst)}%. Showing half as SGST and
            half as CGST.
          </div> */}
        </Card>
      </main>

      {imageModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3">
          <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold">Update Organization Image</h3>
              <button onClick={() => setImageModalOpen(false)}>
                <X size={18} />
              </button>
            </div>

            <div className="mb-6 flex justify-center">
              <div className="h-32 w-32 overflow-hidden rounded-2xl border bg-gray-50">
                {preview || organization?.brand_image ? (
                  <img
                    src={preview || organization?.brand_image}
                    className="h-full w-full object-cover"
                    alt="preview"
                  />
                ) : (
                  <div className="grid h-full w-full place-items-center text-gray-400">
                    <ImagePlus size={34} />
                  </div>
                )}
              </div>
            </div>

            <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed p-4 text-sm font-semibold text-gray-600 hover:border-[#075E54]">
              <Upload size={16} />

              {uploading ? "Uploading..." : "Choose Image"}

              <input
                type="file"
                accept="image/*"
                hidden
                disabled={uploading}
                onChange={handleImageSelect}
              />
            </label>

            {uploading && (
              <p className="mt-4 text-center text-xs text-gray-500">
                Uploading & syncing…
              </p>
            )}
          </div>
        </div>
      )}

      {gstModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3">
          <div className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-5 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold">Edit GST Details</h3>
                <p className="text-xs text-gray-500">
                  Enter total GST only. SGST/CGST will be shown half-half.
                </p>
              </div>
              <button onClick={() => setGstModalOpen(false)}>
                <X size={18} />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="mb-1 block text-xs font-semibold text-gray-500">
                  GST No.
                </label>
                <input
                  type="text"
                  placeholder="Enter GST number"
                  value={gstForm.gst}
                  onChange={(e) =>
                    setGstForm((s) => ({
                      ...s,
                      gst: e.target.value.toUpperCase(),
                    }))
                  }
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:border-[#075E54] focus:outline-none"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-gray-500">
                  GST Type
                </label>
                <select
                  value={gstForm.gstType}
                  onChange={(e) =>
                    setGstForm((s) => ({ ...s, gstType: e.target.value }))
                  }
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:border-[#075E54] focus:outline-none"
                >
                  <option value="incl">Incl</option>
                  <option value="excl">Excl</option>
                </select>
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold text-gray-500">
                  Total GST %
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Example: 5.00"
                  value={gstForm.taxGst}
                  onChange={(e) =>
                    setGstForm((s) => ({ ...s, taxGst: e.target.value }))
                  }
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:border-[#075E54] focus:outline-none"
                />
              </div>

              <div className="rounded-xl border bg-gray-50 px-4 py-3">
                <div className="text-xs font-semibold text-gray-500">SGST</div>
                <div className="mt-1 text-lg font-bold text-gray-900">
                  {formatTax(halfTax)}%
                </div>
              </div>

              <div className="rounded-xl border bg-gray-50 px-4 py-3">
                <div className="text-xs font-semibold text-gray-500">CGST</div>
                <div className="mt-1 text-lg font-bold text-gray-900">
                  {formatTax(halfTax)}%
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setGstModalOpen(false)}
                className="rounded-xl border px-5 py-2.5 text-sm font-semibold hover:bg-gray-50"
              >
                Cancel
              </button>

              <button
                onClick={handleGstUpdate}
                disabled={savingGst || busy?.[clientId] === "gst"}
                className="inline-flex items-center gap-2 rounded-xl bg-[#075E54] px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
              >
                <CheckCircle2 size={16} />
                {savingGst || busy?.[clientId] === "gst"
                  ? "Saving..."
                  : "Update GST"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoBox({ label, value }) {
  return (
    <div className="rounded-2xl border bg-white px-4 py-3">
      <div className="text-xs font-semibold text-gray-500">{label}</div>
      <div className="mt-1 break-all text-sm font-bold text-gray-900">
        {value}
      </div>
    </div>
  );
}

function Card({ title, icon: Icon, rightAction, children }) {
  return (
    <div
      className={`rounded-2xl bg-white p-7 shadow-sm border ${BORDER_SMART}`}
    >
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className={`rounded-xl border bg-[#f8fbff] p-2 text-[#075E54] ${BORDER_SMART}`}
          >
            <Icon size={20} />
          </div>

          <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">
            {title}
          </div>
        </div>

        {rightAction}
      </div>

      <div>{children}</div>
    </div>
  );
}

function LoaderSkeleton() {
  return (
    <div
      className={`min-h-screen space-y-8 p-10 animate-pulse ${THEME_BLUE_BG}`}
    >
      <div className="h-40 w-full rounded-3xl bg-gray-200" />
      <div className={`h-80 rounded-2xl border bg-white ${BORDER_SMART}`} />
    </div>
  );
}
